import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Paper,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { sr } from 'date-fns/locale';
import { useTheme as useAppTheme  } from '../contexts/ThemeContext';
import { createCommonStyles } from '../theme/styles';
import { darkColors, lightColors } from '../theme';
import { getTableHeaderStyles } from '../theme/colors';
import { useSelector } from "react-redux";
import appointmentService from "../services/appointmentService";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

const Statistics = () => {
  const { isDarkMode } = useAppTheme();
  const commonStyles = createCommonStyles(isDarkMode);
  const customColors = isDarkMode ? darkColors : lightColors;

  // State za filtere
  const [dateFrom, setDateFrom] = useState(new Date());
  const [dateTo, setDateTo] = useState(new Date());
  const [selectedPeriod, setSelectedPeriod] = useState('thisWeek');

  // Statistika (placeholder values, popuniti sa stvarnim podacima kasnije)
  const [totalAppointments, setTotalAppointments] = useState(0);
  const [completedAppointments, setCompletedAppointments] = useState(0);
  const [revenue, setRevenue] = useState(0);
  // Previous period stats
  const [prevTotalAppointments, setPrevTotalAppointments] = useState(0);
  const [appointmentsByDate, setAppointmentsByDate] = useState([]);

  const { user: currentUser } = useSelector((state) => state.auth);

  // Opcije za dropdown
  const periodOptions = [
    { value: 'yesterday', label: 'Juče' },
    { value: 'today', label: 'Danas' },
    { value: 'thisWeek', label: 'Ova sedmica' },
    { value: 'lastWeek', label: 'Predhodna sedmica' },
    { value: 'thisMonth', label: 'Ovaj mesec' },
    { value: 'lastMonth', label: 'Predhodni mesec' },
  ];
  console.log('Current user in Statistics:', currentUser);
  // Funkcija za računanje datuma na osnovu izabrane opcije
  const calculateDates = (period) => {
    const today = new Date();
    let from = new Date(today);
    let to = new Date(today);

    switch (period) {
      case 'yesterday':
        from = new Date(today);
        to = new Date(today);
        from.setDate(today.getDate() - 1);
        to.setDate(today.getDate() - 1);
        break;
      case 'today':
        // Već postavljeno
        break;
      case 'thisWeek':
        const firstDayOfWeek = new Date(today);
        const dayOfWeek = today.getDay(); // 0 = nedelja, 1 = ponedeljak, etc.
        const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Koliko dana do ponedeljka
        firstDayOfWeek.setDate(today.getDate() - diffToMonday);
        from = firstDayOfWeek;
        // set 'to' to the end of the week (Sunday)
        const lastDayOfWeek = new Date(firstDayOfWeek);
        lastDayOfWeek.setDate(firstDayOfWeek.getDate() + 6);
        to = lastDayOfWeek;
        break;
      case 'lastWeek':
        const lastWeekStart = new Date(today);
        const lastWeekDayOfWeek = today.getDay();
        const lastWeekDiffToMonday = lastWeekDayOfWeek === 0 ? 6 : lastWeekDayOfWeek - 1;
        lastWeekStart.setDate(today.getDate() - lastWeekDiffToMonday - 7); // Prošli ponedeljak
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6); // Prošla nedelja
        from = lastWeekStart;
        to = lastWeekEnd;
        break;
      case 'thisMonth':
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        to = new Date(today.getFullYear(), today.getMonth() + 1, 0); // Poslednji dan meseca
        break;
      case 'lastMonth':
        from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        to = new Date(today.getFullYear(), today.getMonth(), 0); // Poslednji dan prošlog meseca
        break;
      default:
        break;
    }

    // Postavi vreme na početak dana za "from" i kraj dana za "to"
    from.setHours(0, 0, 0, 0);
    to.setHours(23, 59, 59, 999);

    return { from, to };
  };

  // Handler za promenu perioda
  const handlePeriodChange = (event) => {
    const period = event.target.value;
    setSelectedPeriod(period);
    const { from, to } = calculateDates(period);
    setDateFrom(from);
    setDateTo(to);
  };

  // Inicijalno postavi "Ova sedmica"
  useEffect(() => {
    const { from, to } = calculateDates('thisWeek');
    setDateFrom(from);
    setDateTo(to);
    setSelectedPeriod('thisWeek');
  }, []);

  // Helpers to convert Date <-> input[type=date] value (YYYY-MM-DD)
  const formatDateForInput = (d) => {
    if (!d) return '';
    const dt = d instanceof Date ? d : new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const parseInputDate = (val) => {
    if (!val) return null;
    // new Date('YYYY-MM-DD') creates date in UTC at 00:00 — that's fine for filters
    return new Date(val);
  };

  // display-friendly date (DD.MM.YYYY)
  const formatDisplayDate = (d) => {
    if (!d) return '';
    const dt = d instanceof Date ? d : new Date(d);
    return dt.toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Helpers for chart data
  const getDatesBetween = (start, end) => {
    const dates = [];
    if (!start || !end) return dates;
    const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    while (cur <= last) {
      dates.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return dates;
  };

  const prepareChartData = useCallback(() => {
    const dates = getDatesBetween(dateFrom, dateTo);
    console.log('dates between', dateFrom, 'and', dateTo, ':', dates);
    // map appointmentsByDate array (date string YYYY-MM-DD) to a lookup
    const lookup = {};
    (appointmentsByDate || []).forEach((a) => {
      lookup[a.date] = a.totalAppointments || 0;
    });
    const data = dates.map((d) => {
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
        d.getDate()
      ).padStart(2, '0')}`;
      return { date: key, label: d.toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit' }), value: lookup[key] || 0 };
    });
    return data;
  }, [dateFrom, dateTo, appointmentsByDate]);
  console.log("Preparing chart data with appointmentsByDate:", prepareChartData());
  // Custom tooltip to show date and the appointments count with a colored dot
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const item = payload[0];
    const value = item?.value ?? item?.payload?.value ?? 0;

    // format label (expected YYYY-MM-DD) to DD.MM.YYYY
    let dateLabel = label;
    if (typeof label === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(label)) {
      const [y, m, d] = label.split('-');
      dateLabel = `${d}.${m}.${y}.`;
    } else {
      try {
        const dt = new Date(label);
        dateLabel = dt.toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric' });
      } catch (e) {
        // leave as-is
      }
    }

    return (
      <Box sx={{ p: 1, background: getTableHeaderStyles(isDarkMode).cellBackground, borderRadius: 1, boxShadow: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: commonStyles.textPrimary.color }}>{dateLabel}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: customColors.primary.main, mr: 1 }} />
          <Typography variant="body2" sx={{ color: commonStyles.textPrimary.color }}>{`${value} termina`}</Typography>
        </Box>
      </Box>
    );
  };
  // memoize chart data to avoid recomputing on every render
  const chartData = useMemo(() => prepareChartData(), [prepareChartData]);
  console.log('Prepared chart data:', chartData);
  // percent change between current and previous total appointments
  const percentChange = useMemo(() => {
    const cur = Number(totalAppointments || 0);
    const prev = Number(prevTotalAppointments || 0);
    if (prev === 0) {
      if (cur === 0) return 0;
      return 100; // represent as 100% when previous is 0 and current > 0
    }
    const diff = ((cur - prev) / Math.abs(prev)) * 100;
    return Math.round(diff);
  }, [totalAppointments, prevTotalAppointments]);

  // compute previous period range (prevFrom, prevTo) matching the logic used when fetching stats
  const { prevFrom: computedPrevFrom, prevTo: computedPrevTo } = useMemo(() => {
    if (!dateFrom || !dateTo) return { prevFrom: null, prevTo: null };
    const msPerDay = 24 * 60 * 60 * 1000;
    const fromUTC = Date.UTC(dateFrom.getFullYear(), dateFrom.getMonth(), dateFrom.getDate());
    const toUTC = Date.UTC(dateTo.getFullYear(), dateTo.getMonth(), dateTo.getDate());
    const days = Math.round((toUTC - fromUTC) / msPerDay) + 1;

    const prevToUTC = fromUTC - msPerDay;
    const prevFromUTC = prevToUTC - (days - 1) * msPerDay;

    return { prevFrom: new Date(prevFromUTC), prevTo: new Date(prevToUTC) };
  }, [dateFrom, dateTo]);
  // Fetch statistics whenever date range or selected period changes
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const params = {
          dateFrom: formatDateForInput(dateFrom),
          dateTo: formatDateForInput(dateTo),
        };

        // include tenantId when available
        const tenantId = currentUser?.tenantId?._id || currentUser?.tenantId || null;
        if (tenantId) params.tenantId = tenantId;

        // Calculate previous period: same length directly before current range
        // Use UTC date-only arithmetic to avoid timezone rounding issues
        const msPerDay = 24 * 60 * 60 * 1000;
        const fromUTC = Date.UTC(
          dateFrom.getFullYear(),
          dateFrom.getMonth(),
          dateFrom.getDate()
        );
        const toUTC = Date.UTC(
          dateTo.getFullYear(),
          dateTo.getMonth(),
          dateTo.getDate()
        );
        const days = Math.round((toUTC - fromUTC) / msPerDay) + 1;

        const prevToUTC = fromUTC - msPerDay;
        const prevFromUTC = prevToUTC - (days - 1) * msPerDay;

        const prevTo = new Date(prevToUTC);
        const prevFrom = new Date(prevFromUTC);

        const prevParams = {
          dateFrom: formatDateForInput(prevFrom),
          dateTo: formatDateForInput(prevTo),
        };
        if (tenantId) prevParams.tenantId = tenantId;

        // Run both requests in parallel
        const [resCurrent, resPrev] = await Promise.all([
          appointmentService.getStatistics(params),
          appointmentService.getStatistics(prevParams),
        ]);
        console.log('current stats response:', resCurrent);
        console.log('previous stats response:', resPrev);
        const statsCurrent = resCurrent && resCurrent.data ? resCurrent.data : resCurrent;
        const statsPrev = resPrev && resPrev.data ? resPrev.data : resPrev;

        setTotalAppointments(statsCurrent.totalAppointments ?? statsCurrent.total ?? 0);
        setCompletedAppointments(statsCurrent.completedAppointments ?? statsCurrent.completed ?? 0);
        setRevenue(statsCurrent.revenue ?? statsCurrent.totalRevenue ?? statsCurrent.revenueAmount ?? 0);
        setAppointmentsByDate(statsCurrent.appointmentsByDate || []);

        setPrevTotalAppointments(statsPrev.totalAppointments ?? statsPrev.total ?? 0);
      } catch (err) {
        console.error('Error fetching statistics:', err);
      }
    };

    // Only fetch if both dates present
    if (dateFrom && dateTo) {
      fetchStats();
    }
  }, [dateFrom, dateTo, selectedPeriod, currentUser]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={sr}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
          Statistika
        </Typography>

        {/* Filteri */}
        <Paper sx={{ p: 3, mb: 3, ...commonStyles.card }}>
          <Grid container spacing={3} alignItems="center">
            {/* Period (styled like the date inputs) */}
            <Grid item xs={12} md={3}>
              <Box sx={{ mb: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ ...commonStyles.textPrimary, mb: 1, fontWeight: 600 }}
                >
                  Period
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={selectedPeriod}
                    onChange={handlePeriodChange}
                    displayEmpty
                    sx={{
                      height: 48,
                      "& .MuiSelect-select": {
                        display: 'flex',
                        alignItems: 'center',
                      },
                      "&.MuiOutlinedInput-root": {
                        color: commonStyles.textPrimary.color,
                        background: getTableHeaderStyles(isDarkMode).cellBackground,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: customColors.border.light,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: customColors.primary.main,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: customColors.primary.main,
                        },
                      },
                    }}
                  >
                    {periodOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>

            {/* Styled date input za "Od" */}
            <Grid item xs={12} md={3}>
              <Box sx={{ mb: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ ...commonStyles.textPrimary, mb: 1, fontWeight: 600 }}
                >
                  Datum od
                </Typography>
                <TextField
                  fullWidth
                  type="date"
                  value={formatDateForInput(dateFrom)}
                  onChange={(e) => setDateFrom(parseInputDate(e.target.value))}
                  InputLabelProps={{
                    shrink: true,
                    sx: { color: commonStyles.textSecondary.color },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: commonStyles.textPrimary.color,
                      background: getTableHeaderStyles(isDarkMode).cellBackground,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: customColors.border.light,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: customColors.primary.main,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: customColors.primary.main,
                      },
                    },
                  }}
                />
              </Box>
            </Grid>

            {/* Styled date input za "Do" */}
            <Grid item xs={12} md={3}>
              <Box sx={{ mb: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ ...commonStyles.textPrimary, mb: 1, fontWeight: 600 }}
                >
                  Datum do
                </Typography>
                <TextField
                  fullWidth
                  type="date"
                  value={formatDateForInput(dateTo)}
                  onChange={(e) => setDateTo(parseInputDate(e.target.value))}
                  InputLabelProps={{
                    shrink: true,
                    sx: { color: commonStyles.textSecondary.color },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: commonStyles.textPrimary.color,
                      background: getTableHeaderStyles(isDarkMode).cellBackground,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: customColors.border.light,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: customColors.primary.main,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: customColors.primary.main,
                      },
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Placeholder za statistiku */}
        <Card sx={commonStyles.card}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, borderRadius: 2, height: '100%', background: getTableHeaderStyles(isDarkMode).cellBackground, position: 'relative' }}>
                  <Typography variant="subtitle1" sx={{ color: commonStyles.textSecondary.color, mb: 1, fontSize: '0.975rem', fontWeight: 600 }}>
                    Ukupno termina
                  </Typography>
                  {/* percent comparison badge (top-right) with period */}
                  <Box sx={{ position: 'absolute', top: 12, right: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                      {percentChange > 0 ? (
                        <>
                          <TrendingUpIcon sx={{ color: 'success.main', fontSize: 22 }} />
                          <Typography variant="subtitle2" sx={{ color: 'success.main', fontWeight: 800, fontSize: '0.95rem' }}>{`${Math.abs(percentChange)}%`}</Typography>
                        </>
                      ) : percentChange < 0 ? (
                        <>
                          <TrendingDownIcon sx={{ color: 'error.main', fontSize: 22 }} />
                          <Typography variant="subtitle2" sx={{ color: 'error.main', fontWeight: 800, fontSize: '0.95rem' }}>{`${Math.abs(percentChange)}%`}</Typography>
                        </>
                      ) : (
                        <Typography variant="subtitle2" sx={{ color: commonStyles.textSecondary.color, fontWeight: 700 }}>{`0%`}</Typography>
                      )}
                    </Box>
                    <Typography variant="caption" sx={{ color: commonStyles.textSecondary.color }}>{'u odnosu na period'}</Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: commonStyles.textPrimary.color,
                        mt: 0,
                        fontWeight: 700,
                        fontSize: '0.825rem',
                        letterSpacing: '0.2px',
                      }}
                    >
                      {`${formatDisplayDate(computedPrevFrom)} - ${formatDisplayDate(computedPrevTo)}`}
                    </Typography>
                  </Box>

                  <Typography variant="h5" sx={{ color: commonStyles.textPrimary.color, fontWeight: 700 }}>
                    {totalAppointments}
                  </Typography>
                  {/* Small per-day totals chart */}
                  <Box sx={{ height: 140, mt: 2 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#2b2b2b' : '#f5f5f5'} />
                        {/* hide both axes for a clean curve */}
                        <XAxis dataKey="date" hide />
                        <YAxis hide />
                        <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                        <Line
                          type="monotone"
                          dataKey="value"
                          stroke={customColors.primary.main}
                          strokeWidth={2}
                          dot={false}
                          isAnimationActive={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    height: '100%',
                    background: getTableHeaderStyles(isDarkMode).cellBackground,
                    position: { xs: 'static', md: 'relative' },
                    // ensure some top padding on small screens so the value doesn't collide with the title
                    pt: { xs: 2, md: 2 },
                  }}
                >
                  <Typography variant="subtitle1" sx={{ color: commonStyles.textSecondary.color, mb: 1, fontSize: '0.975rem', fontWeight: 600 }}>
                    Završeni termini
                  </Typography>
                  <Box
                    sx={{
                      position: { xs: 'static', md: 'absolute' },
                      top: { md: '50%' },
                      left: { md: '50%' },
                      transform: { md: 'translate(-50%, -50%)' },
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mt: { xs: 0 },
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        color: commonStyles.textPrimary.color,
                        fontWeight: 700,
                        textAlign: 'center',
                        fontSize: { xs: '1.5rem', md: '2.25rem' },
                      }}
                    >
                      {completedAppointments}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    height: '100%',
                    background: getTableHeaderStyles(isDarkMode).cellBackground,
                    position: { xs: 'static', md: 'relative' },
                    pt: { xs: 2, md: 2 },
                  }}
                >
                  <Typography variant="subtitle1" sx={{ color: commonStyles.textSecondary.color, mb: 1, fontSize: '0.975rem', fontWeight: 600 }}>
                    Prihod
                  </Typography>
                  <Box
                    sx={{
                      position: { xs: 'static', md: 'absolute' },
                      top: { md: '50%' },
                      left: { md: '50%' },
                      transform: { md: 'translate(-50%, -50%)' },
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mt: { xs: 0 },
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        color: commonStyles.textPrimary.color,
                        fontWeight: 700,
                        textAlign: 'center',
                        fontSize: { xs: '1.5rem', md: '2.25rem' },
                      }}
                    >
                      {new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(revenue)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </LocalizationProvider>
  );
};

export default Statistics;
